import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Theme } from '../types';

interface ThemeState {
  theme: Theme;
  isDark: boolean;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  initializeTheme: () => void;
}

const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

const applyTheme = (isDark: boolean) => {
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? '#1e293b' : '#ffffff');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = isDark ? '#1e293b' : '#ffffff';
      document.head.appendChild(meta);
    }
    
    // Update color scheme for better browser integration
    root.style.colorScheme = isDark ? 'dark' : 'light';
  }
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      isDark: false,

      setTheme: (theme: Theme) => {
        const systemTheme = getSystemTheme();
        const isDark = theme === 'dark' || (theme === 'system' && systemTheme === 'dark');
        
        applyTheme(isDark);
        set({ theme, isDark });
        
        console.log(`Theme changed to: ${theme} (${isDark ? 'dark' : 'light'} mode)`);
      },

      toggleTheme: () => {
        const { theme } = get();
        const newTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
        get().setTheme(newTheme);
      },

      initializeTheme: () => {
        const { theme } = get();
        const systemTheme = getSystemTheme();
        const isDark = theme === 'dark' || (theme === 'system' && systemTheme === 'dark');
        
        applyTheme(isDark);
        set({ isDark });

        console.log(`Theme initialized: ${theme} (${isDark ? 'dark' : 'light'} mode)`);

        // Listen for system theme changes
        if (typeof window !== 'undefined' && window.matchMedia) {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          const handleChange = () => {
            const { theme } = get();
            if (theme === 'system') {
              const newIsDark = mediaQuery.matches;
              applyTheme(newIsDark);
              set({ isDark: newIsDark });
              console.log(`System theme changed: ${newIsDark ? 'dark' : 'light'} mode`);
            }
          };
          
          // Remove existing listener if any
          mediaQuery.removeEventListener('change', handleChange);
          mediaQuery.addEventListener('change', handleChange);
          
          return () => mediaQuery.removeEventListener('change', handleChange);
        }
      }
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({ theme: state.theme }),
    }
  )
);